<template lang="pug">
b-modal#staff-create-modal.staff-create-modal(
  title="New Request (on client's behalf)"
  size="xl"
  centered
  scrollable
  no-close-on-backdrop
  hide-footer
  @shown="onShown"
  @hidden="reset"
)
  //- 1 · Request type
  .form-section
    label.section-label 1 · What is this request?
    .type-toggle
      button.type-option(:class="{ active: form.type === 'mc' }" @click="setType('mc')")
        Icon(icon="tabler:medical-cross")
        | MC / Document
      button.type-option(:class="{ active: form.type === 'swap' }" @click="setType('swap')")
        Icon(icon="tabler:repeat")
        | Session Swap

  //- 2 · Client — search list collapses into a clear "selected" card
  .form-section
    label.section-label 2 · Which client?
    .selected-client(v-if="selectedClient")
      Icon.selected-icon(icon="tabler:circle-check-filled")
      .selected-text
        .selected-name {{ selectedClient.no || selectedClient.name }}
        .selected-sub(v-if="selectedClient.name && selectedClient.no") {{ selectedClient.name }}
        .selected-sub(v-else) Selected
      b-button(size="lg" variant="outline-secondary" @click="changeClient") Change client
    template(v-else)
      b-form-input.client-search(
        v-model="clientSearch"
        size="lg"
        autocomplete="off"
        placeholder="Type the client's name to search…"
        ref="clientSearch"
      )
      .client-list(v-if="meta.clients.length")
        button.client-row(v-for="c in filteredClients" :key="c.id" @click="pickClient(c)")
          span.client-row-name {{ c.no || c.name }}
          span.client-row-sub(v-if="c.no && c.name") {{ c.name }}
          Icon.client-row-arrow(icon="tabler:chevron-right")
        .client-empty(v-if="!filteredClients.length") No active client matches "{{ clientSearch }}".
        .client-more(v-else-if="filteredClientsTotal > filteredClients.length") Showing {{ filteredClients.length }} of {{ filteredClientsTotal }} — keep typing to narrow down.
      .client-empty(v-else) Loading clients…

  //- 3a · MC details
  template(v-if="form.type === 'mc'")
    .form-section
      label.section-label 3 · Which dates does the MC cover?
      .calendar-modes
        button.mode-btn(:class="{ active: mcMode === 'days' }" @click="setMcMode('days')")
          Icon(icon="tabler:calendar-event")
          | Tap each day
        button.mode-btn(:class="{ active: mcMode === 'range' }" @click="setMcMode('range')")
          Icon(icon="tabler:calendar-stats")
          | Pick a start and end
      .calendar-hint(v-if="mcMode === 'range' && rangeStart") Start: #[strong {{ formatLong(rangeStart) }}] — now tap the #[strong end date].
      .calendar-hint(v-else-if="mcMode === 'range'") Tap the #[strong first day] of the MC, then the #[strong last day].
      .calendar-hint(v-else) Tap a day to add it; tap again to remove it.
      b-row
        b-col(lg="6")
          b-calendar.big-calendar(
            v-model="calendarPick"
            block
            hide-header
            start-weekday="1"
            locale="en-SG"
            :min="minMcDate"
            :date-info-fn="mcDateClass"
            label-help=""
            @selected="onMcCalendarPick"
          )
        b-col(lg="6")
          .date-summary
            .date-summary-title
              | Selected: #[strong {{ form.dates.length }}] day{{ form.dates.length === 1 ? '' : 's' }}
              b-button.ml-auto(size="sm" variant="link" v-if="form.dates.length" @click="form.dates = []") Clear all
            .date-chips(v-if="form.dates.length")
              span.date-chip(v-for="d in form.dates" :key="d")
                | {{ formatChip(d) }}
                button.chip-x(@click="removeDate(d)" :aria-label="'Remove ' + d") ×
            .text-muted(v-else) No dates yet. Max 60 days.

    .form-section
      label.section-label 4 · Reason
      b-form-textarea(v-model="form.reason" rows="3" size="lg" placeholder="e.g. MC submitted physically at the centre (at least 5 characters)")

    .form-section
      label.section-label 5 · MC proof
        span.text-danger.font-weight-normal  (required — a PDF or photo of the MC)
      b-form-file(v-model="form.files" multiple accept=".pdf,.jpg,.jpeg,.png" size="lg" placeholder="Choose PDF / image…" browse-text="Browse")
      .field-hint PDF / JPG / PNG, up to 5 files, 4 MB in total.
      .field-hint.text-danger(v-if="filesTooBig") All files together must be under 4 MB (currently {{ filesTotalMb }} MB) — use a smaller photo or fewer files.
      .field-hint.text-danger(v-else-if="!hasProof && !form.noProof") Attach the proof, or tick the box below if the physical MC is held at the centre.
      b-form-checkbox.big-check.mt-2(v-model="form.noProof" :disabled="hasProof" size="lg")
        | Submit without proof (staff holds the physical MC / will attach it later)

    .form-section
      label.section-label 6 · Alert the caregiver?
      b-form-checkbox.big-check(v-model="form.notifyOnOutcome" size="lg")
        | Send the caregiver an app notification + email when this request is approved or rejected

  //- 3b · Swap details
  template(v-else)
    b-row
      b-col(lg="6")
        .form-section
          label.section-label 3 · Session the client will miss
          b-form-select(v-model="form.scheduleId" :options="clientScheduleOptions" size="lg" @change="form.originalDate = ''")
          .field-hint(v-if="!form.clientId") Pick the client first.
          .field-hint(v-else-if="form.scheduleId") Only {{ scheduleDayName(form.scheduleId) }}s from tomorrow can be picked.
          b-calendar.big-calendar.mt-2(
            v-if="form.scheduleId"
            v-model="form.originalDate"
            block
            hide-header
            start-weekday="1"
            locale="en-SG"
            :min="tomorrow"
            :date-disabled-fn="originalDateDisabled"
            label-help=""
          )
          .picked-date(v-if="form.originalDate")
            Icon(icon="tabler:circle-check-filled")
            | Missing: #[strong {{ formatLong(form.originalDate) }}]
      b-col(lg="6")
        .form-section
          label.section-label 4 · Replacement session
          b-form-select(v-model="form.replacementScheduleId" :options="allScheduleOptions" size="lg" @change="form.replacementDate = ''")
          .field-hint(v-if="form.replacementScheduleId") Only {{ scheduleDayName(form.replacementScheduleId) }}s after the missed session can be picked. Staff may choose any session.
          b-calendar.big-calendar.mt-2(
            v-if="form.replacementScheduleId"
            v-model="form.replacementDate"
            block
            hide-header
            start-weekday="1"
            locale="en-SG"
            :min="replacementMin"
            :date-disabled-fn="replacementDateDisabled"
            label-help=""
          )
          .picked-date(v-if="form.replacementDate")
            Icon(icon="tabler:circle-check-filled")
            | Attending instead: #[strong {{ formatLong(form.replacementDate) }}]

    .form-section
      label.section-label 5 · Note (optional)
      b-form-input(v-model="form.reason" size="lg" placeholder="e.g. requested by phone")
      .field-hint The swap is applied to attendance automatically by the nightly processor.

  //- Submit
  .modal-actions
    b-button(size="lg" variant="outline-secondary" @click="$bvModal.hide('staff-create-modal')" :disabled="saving") Cancel
    b-button(size="lg" variant="primary" @click="submit" :disabled="saving || !canSubmit")
      b-spinner(small v-if="saving")
      span(v-else) {{ form.type === 'mc' ? 'Submit MC Request' : 'Create Session Swap' }}
  .field-hint.text-right.mt-1(v-if="!canSubmit && !saving") {{ missingHint }}
</template>

<script>
import { Icon } from '@iconify/vue2';
import { mapActions } from 'vuex';
import { MAKEUP_REQUESTS_NAMESPACE, ACTIONS } from '@/store/Modules/MakeupRequests/types-makeup-requests';
import dayjs from 'dayjs';

const MAX_LIST = 40;

export default {
  name: 'StaffCreateRequestModal',
  components: { Icon },
  data() {
    return {
      meta: { clients: [], schedules: [] },
      clientScheduleIds: [],
      clientSearch: '',
      mcMode: 'days',
      rangeStart: '',
      calendarPick: '',
      saving: false,
      form: {
        type: 'mc',
        clientId: null,
        dates: [],
        reason: '',
        files: [],
        // Staff-created MCs default to NOT alerting the caregiver on approval —
        // the family was usually informed directly at the centre.
        notifyOnOutcome: false, noProof: false,
        scheduleId: null,
        originalDate: '',
        replacementScheduleId: null,
        replacementDate: ''
      }
    };
  },
  computed: {
    selectedClient() {
      return this.meta.clients.find(c => c.id === this.form.clientId) || null;
    },
    filteredClientsAll() {
      const q = this.clientSearch.trim().toLowerCase();
      // staff-meta already returns ACTIVE clients only
      return this.meta.clients.filter(c => !q || `${c.no} ${c.name}`.toLowerCase().includes(q));
    },
    filteredClientsTotal() { return this.filteredClientsAll.length; },
    filteredClients() { return this.filteredClientsAll.slice(0, MAX_LIST); },

    scheduleLabel() {
      return (s) => `${s.name}${s.dayName ? ` (${s.dayName}${s.time ? ' ' + s.time : ''})` : ''}`;
    },
    scheduleById() {
      return Object.fromEntries(this.meta.schedules.map(s => [s.id, s]));
    },
    clientScheduleOptions() {
      const own = this.meta.schedules.filter(s => this.clientScheduleIds.includes(s.id));
      return [
        { value: null, text: own.length ? 'Choose the session…' : (this.form.clientId ? 'Loading the client\'s sessions…' : 'Pick a client first…') },
        ...own.map(s => ({ value: s.id, text: this.scheduleLabel(s) }))
      ];
    },
    allScheduleOptions() {
      return [
        { value: null, text: 'Choose the replacement session…' },
        ...this.meta.schedules.map(s => ({ value: s.id, text: this.scheduleLabel(s) }))
      ];
    },

    tomorrow() { return dayjs().add(1, 'day').format('YYYY-MM-DD'); },
    minMcDate() { return dayjs().startOf('month').format('YYYY-MM-DD'); },
    replacementMin() {
      return this.form.originalDate ? dayjs(this.form.originalDate).add(1, 'day').format('YYYY-MM-DD') : this.tomorrow;
    },

    hasProof() {
      return Array.isArray(this.form.files) ? this.form.files.length > 0 : !!this.form.files;
    },
    filesTotalBytes() {
      const list = Array.isArray(this.form.files) ? this.form.files : (this.form.files ? [this.form.files] : []);
      return list.reduce((t, f) => t + (f.size || 0), 0);
    },
    filesTotalMb() { return (this.filesTotalBytes / 1024 / 1024).toFixed(1); },
    filesTooBig() { return this.filesTotalBytes > 4 * 1024 * 1024; },
    canSubmit() {
      if (!this.form.clientId) return false;
      if (this.form.type === 'mc') {
        return this.form.dates.length > 0 && this.form.dates.length <= 60 && this.form.reason.trim().length >= 5 && (this.hasProof || this.form.noProof) && !this.filesTooBig;
      }
      return !!(this.form.scheduleId && this.form.originalDate && this.form.replacementScheduleId && this.form.replacementDate);
    },
    missingHint() {
      if (!this.form.clientId) return 'Pick a client to continue.';
      if (this.form.type === 'mc') {
        if (!this.form.dates.length) return 'Add at least one MC date.';
        if (this.form.dates.length > 60) return 'An MC cannot exceed 60 days.';
        if (this.form.reason.trim().length < 5) return 'Enter a reason (at least 5 characters).';
        if (this.filesTooBig) return 'The attached files are over 4 MB in total.';
        if (!this.hasProof && !this.form.noProof) return 'Attach the MC proof, or tick "Submit without proof".';
        return '';
      }
      if (!this.form.scheduleId) return 'Choose the session the client will miss.';
      if (!this.form.originalDate) return 'Tap the date of the missed session on the calendar.';
      if (!this.form.replacementScheduleId) return 'Choose the replacement session.';
      if (!this.form.replacementDate) return 'Tap the replacement date on the calendar.';
      return '';
    }
  },
  methods: {
    ...mapActions(MAKEUP_REQUESTS_NAMESPACE, {
      getStaffMeta: ACTIONS.GET_STAFF_META,
      staffCreateAction: ACTIONS.STAFF_CREATE
    }),

    async onShown() {
      if (!this.meta.clients.length) {
        try {
          this.meta = await this.getStaffMeta();
        } catch (e) {
          this.$bvToast.toast('Failed to load clients/schedules', { title: 'Error', variant: 'danger', solid: true });
        }
      }
      this.$nextTick(() => this.$refs.clientSearch && this.$refs.clientSearch.focus());
    },

    setType(type) { this.form.type = type; },

    async pickClient(c) {
      this.form.clientId = c.id;
      this.clientSearch = '';
      this.clientScheduleIds = [];
      this.form.scheduleId = null;
      this.form.originalDate = '';
      try {
        const data = await this.getStaffMeta(c.id);
        this.clientScheduleIds = data.clientScheduleIds || [];
      } catch (e) {
        console.error('Failed to load client schedules:', e);
      }
    },
    changeClient() {
      this.form.clientId = null;
      this.clientScheduleIds = [];
      this.form.scheduleId = null;
      this.form.originalDate = '';
      this.$nextTick(() => this.$refs.clientSearch && this.$refs.clientSearch.focus());
    },

    // ---- MC calendar
    setMcMode(mode) { this.mcMode = mode; this.rangeStart = ''; },
    mcDateClass(ymd) {
      if (this.form.dates.includes(ymd)) return 'mc-day-selected';
      if (this.mcMode === 'range' && this.rangeStart === ymd) return 'mc-day-start';
      return '';
    },
    onMcCalendarPick(ymd) {
      if (!ymd) return;
      if (this.mcMode === 'days') {
        this.form.dates = this.form.dates.includes(ymd)
          ? this.form.dates.filter(d => d !== ymd)
          : [...this.form.dates, ymd].sort();
      } else if (!this.rangeStart) {
        this.rangeStart = ymd;
      } else {
        let cur = dayjs(this.rangeStart);
        let end = dayjs(ymd);
        if (end.isBefore(cur)) [cur, end] = [end, cur];
        const set = new Set(this.form.dates);
        while (cur.isBefore(end) || cur.isSame(end, 'day')) {
          set.add(cur.format('YYYY-MM-DD'));
          cur = cur.add(1, 'day');
        }
        this.form.dates = [...set].sort().slice(0, 60);
        this.rangeStart = '';
      }
      // keep the calendar "unselected" so the same day can be tapped again
      this.$nextTick(() => { this.calendarPick = ''; });
    },
    removeDate(d) { this.form.dates = this.form.dates.filter(x => x !== d); },
    formatChip(d) { return dayjs(d).format('ddd D MMM'); },
    formatLong(d) { return dayjs(d).format('dddd, D MMMM YYYY'); },

    // ---- Swap calendars: only the schedule's weekday is selectable
    scheduleDayName(id) { return this.scheduleById[id]?.dayName || 'session day'; },
    originalDateDisabled(ymd) {
      const s = this.scheduleById[this.form.scheduleId];
      return s && s.day != null ? dayjs(ymd).day() !== (s.day % 7) : false;
    },
    replacementDateDisabled(ymd) {
      const s = this.scheduleById[this.form.replacementScheduleId];
      return s && s.day != null ? dayjs(ymd).day() !== (s.day % 7) : false;
    },

    async submit() {
      if (!this.canSubmit || this.saving) return;
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append('type', this.form.type);
        fd.append('clientId', this.form.clientId);
        fd.append('reason', this.form.reason.trim());
        if (this.form.type === 'mc') {
          fd.append('dates', JSON.stringify(this.form.dates));
          fd.append('notifyOnOutcome', this.form.notifyOnOutcome ? 'true' : 'false');
          fd.append('noProof', this.form.noProof && !this.hasProof ? 'true' : 'false');
          (this.form.files || []).forEach(f => fd.append('documents', f));
        } else {
          fd.append('scheduleId', this.form.scheduleId);
          fd.append('originalDate', this.form.originalDate);
          fd.append('replacementScheduleId', this.form.replacementScheduleId);
          fd.append('replacementDate', this.form.replacementDate);
        }
        await this.staffCreateAction(fd);
        this.$bvToast.toast(
          this.form.type === 'mc'
            ? 'MC request created — it now awaits approval like any other MC.'
            : 'Session swap created — the nightly processor will apply it to attendance.',
          { title: 'Created', variant: 'success', solid: true }
        );
        this.$emit('created');
        this.$bvModal.hide('staff-create-modal');
      } catch (error) {
        this.$bvToast.toast(error?.response?.data?.error || 'Failed to create the request', {
          title: 'Error', variant: 'danger', solid: true, noAutoHide: true
        });
      } finally {
        this.saving = false;
      }
    },

    reset() {
      this.clientSearch = '';
      this.mcMode = 'days';
      this.rangeStart = '';
      this.calendarPick = '';
      this.clientScheduleIds = [];
      this.form = {
        type: 'mc', clientId: null, dates: [], reason: '', files: [], notifyOnOutcome: false, noProof: false,
        scheduleId: null, originalDate: '', replacementScheduleId: null, replacementDate: ''
      };
    }
  }
};
</script>

<style>
/* Larger, high-contrast controls for staff who find small UI hard to read. */
.staff-create-modal .modal-body { font-size: 1.1rem; padding: 1.5rem 1.75rem; }
.staff-create-modal .modal-title { font-size: 1.3rem; font-weight: 700; }
.staff-create-modal .big-calendar .b-calendar-grid { font-size: 1.05rem; }
.staff-create-modal .big-calendar .b-calendar-grid-body .btn { min-height: 2.6rem; font-size: 1rem; }
.staff-create-modal .big-calendar .b-calendar-nav .btn { min-height: 2.4rem; }
.staff-create-modal .big-calendar .mc-day-selected .btn,
.staff-create-modal .big-calendar .mc-day-selected .btn.btn-outline-light {
  background: #50276b; color: #fff; font-weight: 700; border-radius: 8px;
}
.staff-create-modal .big-calendar .mc-day-start .btn { outline: 3px solid #50276b; border-radius: 8px; font-weight: 700; }
.staff-create-modal .big-check .custom-control-label { font-size: 1.02rem; padding-top: 0.1rem; }
</style>

<style scoped>
.form-section { margin-bottom: 1.4rem; }
.section-label {
  display: block;
  font-size: 0.95rem;
  font-weight: 800;
  color: #50276b;
  margin-bottom: 0.5rem;
}
.field-hint { font-size: 0.9rem; color: #6b7280; margin-top: 0.3rem; }

.type-toggle { display: flex; gap: 0.6rem; }
.type-option {
  display: inline-flex; align-items: center; gap: 0.5rem;
  padding: 0.75rem 1.4rem; font-size: 1.05rem; font-weight: 700;
  border: 2px solid #e5e7eb; border-radius: 10px; background: #fff; color: #6b7280; cursor: pointer;
}
.type-option.active { background: #50276b; border-color: #50276b; color: #fff; }

/* client picker */
.client-search { font-size: 1.1rem; }
.client-list { border: 2px solid #e5e7eb; border-radius: 10px; margin-top: 0.5rem; max-height: 320px; overflow-y: auto; }
.client-row {
  display: flex; align-items: center; gap: 0.6rem; width: 100%; text-align: left;
  padding: 0.75rem 1rem; background: #fff; border: none; border-bottom: 1px solid #f0edf4;
  font-size: 1.08rem; cursor: pointer;
}
.client-row:hover, .client-row:focus { background: #f3ecf9; outline: none; }
.client-row-name { font-weight: 700; color: #222; }
.client-row-sub { color: #6b7280; font-size: 0.9rem; }
.client-row-arrow { margin-left: auto; color: #9ca3af; }
.client-empty, .client-more { padding: 0.75rem 1rem; color: #6b7280; font-size: 0.95rem; }
.selected-client {
  display: flex; align-items: center; gap: 0.9rem;
  background: #eaf7ef; border: 2px solid #198754; border-radius: 12px; padding: 0.9rem 1.1rem;
}
.selected-icon { color: #198754; font-size: 2rem; flex-shrink: 0; }
.selected-text { flex: 1; min-width: 0; }
.selected-name { font-size: 1.25rem; font-weight: 800; color: #0f5132; }
.selected-sub { color: #3d6b50; font-size: 0.95rem; }

/* calendars */
.calendar-modes { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; }
.mode-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.5rem 1rem; font-size: 1rem; font-weight: 700;
  border: 2px solid #e5e7eb; border-radius: 999px; background: #fff; color: #6b7280; cursor: pointer;
}
.mode-btn.active { background: #efe6f7; border-color: #50276b; color: #50276b; }
.calendar-hint { font-size: 1rem; color: #444; margin-bottom: 0.5rem; }
.date-summary { background: #fafafc; border: 1px solid #ececf1; border-radius: 12px; padding: 0.9rem 1rem; min-height: 100%; }
.date-summary-title { display: flex; align-items: center; font-size: 1.05rem; margin-bottom: 0.6rem; }
.date-chips { display: flex; flex-wrap: wrap; gap: 0.45rem; }
.date-chip {
  display: inline-flex; align-items: center; gap: 0.35rem;
  background: #efe6f7; color: #50276b; border-radius: 999px;
  padding: 0.3rem 0.4rem 0.3rem 0.8rem; font-size: 1rem; font-weight: 700;
}
.chip-x { border: none; background: #fff; color: #50276b; font-weight: 800; cursor: pointer; line-height: 1; width: 1.6rem; height: 1.6rem; border-radius: 50%; font-size: 1.1rem; }
.picked-date { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.6rem; font-size: 1.05rem; color: #0f5132; background: #eaf7ef; border-radius: 10px; padding: 0.6rem 0.9rem; }

.modal-actions { display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 1.25rem; }
.modal-actions .btn { font-size: 1.1rem; font-weight: 700; padding: 0.6rem 1.6rem; }
</style>
